import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useClientTable, type AppColumnDef, type AppRow } from "../../../components/table/tableFeatures";
import { type TFunction } from "i18next";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { type OrgUser } from "../../../zodios/apiDefinitions/api/userManagement";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { MdRotateLeft } from "react-icons/md";
import { Button } from "../../../components/core/basic/Button";
import { resetTable, selectionStateToSelectedIds } from "../../../components/table/utils";
import { SearchInput } from "../../../components/fields/base/TextField";
import { useModifyUserActiveStatusMutation } from "../api/useModifyUserActiveStatusMutation";
import { useUserRoleOptions } from "../api/useUserRoleOptions";
import ConfirmationModal from "../../../components/core/blocks/ConfirmationModal";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { useSimpleCan } from "../../../config/casl/casl";
import { useAppSelector } from "../../../redux/hooks";
import RoleCell from "./RoleCell";
import Skeleton from "react-loading-skeleton";
import { Link } from "@tanstack/react-router";
import { BulkActionButton } from "../../../components/table/BulkActionButton";
import OrgSelectionTable from "../../../components/custom/staff/OrgSelectionTable";
import { useBulkMutateUserRolesMutation } from "../api/useBulkMutateUserRolesMutation";
import { DEFAULT_ROLE_NAME, ROLES } from "../constants";
import { type OrgRoleSelection } from "../types";
import { type Organization } from "../../../zodios/common/orgSchema";
import { Select } from "../../../components/fields/base/Select";
import { useManagedUserOrgRoles } from "../api/useManagedUserOrgRoles";
import AgencyFilter from "./AgencyFilter";
import UserPermissionsSection from "./UserPermissionsSection";
import AddUsersToOrgModal from "./AddUsersToOrgModal";

const columnHelper = createColumnBuilder<OrgUser>();

export const ROLE_LABEL_LOOKUP = {
  read_only: "Read Only",
  modify: "Modify",
  org_admin: "Org Admin",
};

export type RoleName = keyof typeof ROLE_LABEL_LOOKUP;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getRoleLabel = (t: TFunction<any, any>, roleName: RoleName): string => {
  switch (roleName) {
    case "read_only":
      return t(($) => $.user_management["Read Only"]);
    case "modify":
      return t(($) => $.user_management["Modify"]);
    case "org_admin":
      return t(($) => $.user_management["Org Admin"]);
  }
};

export const UserRolesTooltip = () => {
  const { t } = useTranslation(["user_management"]);
  return (
    <HoverInfo iconCss={{ color: "grey" }}>
      <VStack className={css({ maxWidth: "400px" })}>
        <span>{t(($) => $.user_management["There are 3 supported user roles:"])}</span>
        <ol className={css({ display: "flex", gap: "small", flexDirection: "column", paddingTop: "small" })}>
          <li>
            {t(
              ($) =>
                $.user_management[
                  "Org Admin: full access to all features and settings, including billing, API connection, and user management pages."
                ],
            )}
          </li>
          <li>
            {t(
              ($) =>
                $.user_management[
                  "Modify: edit and read access, restricted from admin pages, such as billing, API connections, and user management."
                ],
            )}
          </li>
          <li>{t(($) => $.user_management["Read Only: can view reports but cannot make changes."])}</li>
        </ol>
      </VStack>
    </HoverInfo>
  );
};

const OrgUsersTable = ({ userData, isActiveUsersTable }: { userData: OrgUser[]; isActiveUsersTable: boolean }) => {
  const { t } = useTranslation(["user_management", "common"]);
  const dateFormatter = useDateFormatter("medium");
  const user = useAppSelector((state) => state.user);

  const defaultAgencyId = user.account_manager.agency_id ?? "none";
  const defaultShowAgencyFilter = user.is_staff && defaultAgencyId !== "none";

  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);
  const [orgRoles, setOrgRoles] = useState<OrgRoleSelection>({});

  const [selectedAgencyId, setSelectedAgencyId] = useState<number | "none">(defaultAgencyId);
  const [showAgencyFilter, setShowAgencyFilter] = useState(defaultShowAgencyFilter);
  const [showAddUserToOrgModal, setShowAddUserToOrgModal] = useState(false);

  const canModifyRoles = useSimpleCan("update", "User");
  const canDeleteUsers = useSimpleCan("delete", "User");

  const userRoleOptionsQuery = useUserRoleOptions();
  const bulkMutateUserRolesMutation = useBulkMutateUserRolesMutation();

  const enableBulkPermissionsManagement = isActiveUsersTable && user.is_staff && canModifyRoles;

  const userOrgRolesQuery = useManagedUserOrgRoles(
    selectedAgencyId !== "none" && showAgencyFilter ?
      {
        agencyId: selectedAgencyId,
      }
    : {},
  );

  const orgSelectorTableData = useMemo(() => {
    if (!userOrgRolesQuery.data) return [];

    return userOrgRolesQuery.data.orgs;
  }, [userOrgRolesQuery.data]);

  const roleOptions = useMemo(() => {
    if (!userRoleOptionsQuery.data) {
      return [];
    }
    return userRoleOptionsQuery.data.roles
      .map((role) => ({
        label: getRoleLabel(t, role.role_name as keyof typeof ROLE_LABEL_LOOKUP),
        value: role.id,
      }))
      .filter((role) => Boolean(role.label));
  }, [userRoleOptionsQuery.data, t]);

  const orgHasSingleAdminUser = useMemo(() => {
    const adminUsers = userData.filter((user) => user.roles.some((role) => role.role_name === "org_admin"));
    return adminUsers.length === 1;
  }, [userData]);

  const columns = useMemo(() => {
    const columnDefs = [
      columnHelper.accessor("first_name", {
        header: t(($) => $.user_management["Name"]),
        cell: (props) => (
          <Link to="/user/userdetails/$id" params={{ id: String(props.row.original.id) }}>
            {props.row.original.first_name} {props.row.original.last_name}
          </Link>
        ),
      }),
      columnHelper.accessor("email", {
        header: t(($) => $.user_management["Email"]),
        size: 300,
      }),
      columnHelper.accessor("last_login", {
        header: t(($) => $.user_management["Last Login"]),
        cell: (row) => {
          const value = row.getValue();
          if (!value) {
            return null;
          }
          return dateFormatter.format(new Date(value));
        },
      }),
      columnHelper.display({
        id: "role",
        header: t(($) => $.user_management["Role"]),
        size: 260,
        minSize: 260,
        maxSize: 260,
        enableHiding: true,
        cell: (props) => {
          const initRole = props.row.original.roles[0];

          if (userRoleOptionsQuery.isLoading) {
            return (
              <Flex className={css({ gap: "medium", alignItems: "center", paddingY: "xsmall", paddingX: "medium" })}>
                <Skeleton width="150px" height="24px" />
              </Flex>
            );
          }

          return (
            <RoleCell
              row={props.row}
              roleOptions={roleOptions}
              isSelectDisabled={!canModifyRoles || (orgHasSingleAdminUser && initRole?.role_name === "org_admin")}
              isConfirmButtonDisabled={
                !canModifyRoles || (orgHasSingleAdminUser && initRole?.role_name === "org_admin")
              }
            />
          );
        },
      }),
      columnHelper.display({
        id: "toggleUserActiveStatus",
        header: t(($) => $.user_management["Actions"]),
        maxSize: 90,
        minSize: 90,
        cell: function UserStatusCell(props) {
          const { id } = props.row.original;
          const { mutate: mutateUserActiveStatus } = useModifyUserActiveStatusMutation();
          return (
            <div className={css({ paddingY: "small" })}>
              <ConfirmationModal
                disabled={
                  !canDeleteUsers ||
                  parseInt(user.user_id) === id ||
                  (isActiveUsersTable &&
                    orgHasSingleAdminUser &&
                    props.row.original.roles.some((role) => role.role_name === "org_admin"))
                }
                title={
                  isActiveUsersTable ?
                    t(($) => $.user_management["Confirm User Deactivation"])
                  : t(($) => $.user_management["Confirm User Reactivation"])
                }
                confirmButtonText={t(($) => $.user_management["Confirm"])}
                confirmationMessage={
                  isActiveUsersTable ?
                    t(($) => $.user_management["Are you sure you want to deactivate the selected user?"])
                  : t(($) => $.user_management["Are you sure you want to reactivate the selected user?"])
                }
                modalButtonProps={{ size: "small" }}
                modalButtonText={
                  isActiveUsersTable ?
                    t(($) => $.user_management["Deactivate"])
                  : t(($) => $.user_management["Reactivate"])
                }
                onConfirm={() => {
                  mutateUserActiveStatus({
                    user_id: id,
                    is_active: !isActiveUsersTable,
                  });
                }}
              />
            </div>
          );
        },
      }),
    ];

    return columnDefs;
  }, [
    userRoleOptionsQuery.isLoading,
    roleOptions,
    canModifyRoles,
    orgHasSingleAdminUser,
    canDeleteUsers,
    user.user_id,
    isActiveUsersTable,
    dateFormatter,
    t,
  ]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: isActiveUsersTable ? "activeUsersTable" : "inactiveUsersTable",
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 5,
      },
    },
  });

  const table = useClientTable({
    data: userData,
    columns,
    globalFilterFn: (row, columnId, filterValue) => {
      const user = row.original;

      if (!filterValue) {
        return true;
      }

      const { first_name, last_name, email } = user;
      const matchable = `${first_name} ${last_name} ${email}`.toLowerCase();
      const searchable: string[] = filterValue.split(",");

      return searchable.some((searchTerm) => matchable.includes(searchTerm.trim().toLowerCase()));
    },
    getRowId: (row) => {
      return row.id.toString();
    },
    enableRowSelection: enableBulkPermissionsManagement,
    getRowCanExpand: () => enableBulkPermissionsManagement,
    initialState,
    state,
    ...setters,
  });

  const selected = selectionStateToSelectedIds(table.state.rowSelection);

  const roleColumn = useMemo<AppColumnDef<Organization>>(
    () => ({
      id: "role",
      header: t(($) => $.user_management["Role"]),
      size: 200,
      minSize: 200,
      maxSize: 200,
      cell: ({ row }) => {
        const orgId = row.original.id;

        if (!selectedOrgIds.includes(orgId)) {
          return null;
        }

        return (
          <Select
            items={ROLES.map((r) => ({
              label: getRoleLabel(t, r.value as keyof typeof ROLE_LABEL_LOOKUP),
              value: r.value,
            }))}
            selectedKey={orgRoles[orgId]}
            onSelectionChange={(value) => {
              if (!value) return;

              setOrgRoles((prev) => ({
                ...prev,
                [orgId]: String(value),
              }));
            }}
            css={{ width: "150px", paddingX: "large" }}
          />
        );
      },
    }),
    [orgRoles, selectedOrgIds, t],
  );

  const handleOrgChange = (ids: number[]) => {
    setSelectedOrgIds(ids);

    setOrgRoles((prev) => {
      const updatedOrgRoles: Record<number, string> = Object.fromEntries(
        ids.map((id) => [id, prev[id] ?? DEFAULT_ROLE_NAME]),
      );

      return updatedOrgRoles;
    });
  };

  const bulkActionOptions = useMemo(() => {
    return [
      {
        label: t(($) => $.user_management["Assign User Roles"]),
        action: () => {
          const body = {
            upserts: selected.flatMap((userId) =>
              Object.entries(orgRoles).map(([orgId, roleName]) => ({
                user_id: Number(userId),
                org_id: Number(orgId),
                role_name: roleName,
              })),
            ),
            creates: [],
            updates: [],
            deletes: [],
          };

          bulkMutateUserRolesMutation.mutate(body, {
            onSuccess: () => {
              setSelectedOrgIds([]);
              setOrgRoles({});
              table.resetRowSelection();
            },
          });
        },
        actionFormFields: (
          <div className={css({ maxHeight: "60vh", paddingRight: "large", overflowY: "auto" })}>
            {user.is_staff && (
              <AgencyFilter
                show={showAgencyFilter}
                value={selectedAgencyId}
                onToggle={() => {
                  setSelectedOrgIds([]);
                  setOrgRoles({});
                  setSelectedAgencyId("none");
                  setShowAgencyFilter((prev) => !prev);
                }}
                onChange={(agencyId) => {
                  setSelectedOrgIds([]);
                  setOrgRoles({});
                  setSelectedAgencyId(agencyId);
                }}
              />
            )}
            <OrgSelectionTable
              onChange={handleOrgChange}
              selectedOrgIds={selectedOrgIds}
              displaySelectOrgFilters={false}
              additionalColumns={[roleColumn]}
              orgs={orgSelectorTableData}
              isLoading={userOrgRolesQuery.isLoading}
              enableHiddenOrgsFilter
            />
          </div>
        ),
        confirmationMessage: t(
          ($) =>
            $.user_management[
              "Are you sure you want to update the selected users' roles in the selected organizations?"
            ],
          { count: selected.length },
        ),
        disableConfirmButton: selectedOrgIds.length === 0 || Object.values(orgRoles).some((role) => !role),
        onClose: () => {
          setSelectedOrgIds([]);
          setOrgRoles({});
        },
      },
    ];
  }, [
    roleColumn,
    orgRoles,
    selected,
    selectedOrgIds,
    bulkMutateUserRolesMutation,
    showAgencyFilter,
    selectedAgencyId,
    orgSelectorTableData,
    user.is_staff,
    table,
    userOrgRolesQuery.isLoading,
    t,
  ]);

  return (
    <>
      {enableBulkPermissionsManagement && (
        <Flex gap={"large"} justifyContent={"flex-end"} paddingTop={"medium"}>
          <Button onPress={() => setShowAddUserToOrgModal(true)}>
            {t(($) => $.user_management["Add Users to Current Org"])}
          </Button>
          <BulkActionButton options={bulkActionOptions} selected={selected} />
        </Flex>
      )}
      {showAddUserToOrgModal && (
        <AddUsersToOrgModal isOpen={showAddUserToOrgModal} onClose={() => setShowAddUserToOrgModal(false)} />
      )}
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: { base: "0 1 150px", md: "0 1 350px" } }}
              value={state.globalFilter ?? ""}
              onChange={(value) => {
                setters.onGlobalFilterChange(value);
              }}
            />
          </HStack>
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        renderExpander={
          enableBulkPermissionsManagement ?
            (row: AppRow<OrgUser>) => (
              <UserPermissionsSection userId={row.original.id.toString()} persistKey={"orgUsersTableExpander"} />
            )
          : undefined
        }
      />
    </>
  );
};

export default OrgUsersTable;
